import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { faxHealth, faxSyncWebhook } from "../../lib/api";

export const FaxHealthCard = () => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data: h, isLoading } = useQuery({ queryKey: ["fax-health"], queryFn: () => faxHealth(false) });
  const refresh = async () => { setBusy(true); try { qc.setQueryData(["fax-health"], await faxHealth(true)); } finally { setBusy(false); } };
  const canFix = h?.app_host && window.location.host.replace(/^www\./, "") === h.app_host.replace(/^www\./, "");
  const fix = async () => {
    setBusy(true);
    try { await faxSyncWebhook(); toast.success("Incoming faxes fixed"); qc.setQueryData(["fax-health"], await faxHealth(true)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update the provider"); }
    finally { setBusy(false); }
  };
  if (isLoading || !h) return <div className="mt-5 rounded-2xl border border-border bg-card p-4"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>;
  return (
    <section className={`mt-5 rounded-2xl border p-4 ${h.healthy ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`} data-testid="fax-health-card">
      <div className="flex items-center gap-2">
        {h.healthy ? <CheckCircle2 className="h-5 w-5 text-emerald-700" /> : <AlertTriangle className="h-5 w-5 text-amber-700" />}
        <h2 className="font-medium" data-testid="fax-health-status">{h.healthy ? (h.warnings ? "Fax line healthy · 1 thing to look at" : "Fax line healthy") : "Fax line needs attention"}</h2>
        <button type="button" onClick={refresh} disabled={busy} className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary disabled:opacity-50" data-testid="fax-health-refresh"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Check again</button>
      </div>
      <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {h.checks.map((c) => (
          <li key={c.key} className="flex items-start gap-2 text-xs" data-testid={`fax-health-${c.key}`}>
            {c.ok && !c.warn ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700 mt-0.5 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-700 mt-0.5 shrink-0" />}
            <span><span className="font-medium">{c.label}</span>{c.detail && <span className="text-muted-foreground"> · {c.detail}</span>}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[10px] text-muted-foreground">Checked {new Date(h.checked_at).toLocaleString()} · no fax is sent and nothing is charged.</p>
      {h.checks.some((c) => c.key === "webhook" && !c.ok) && (
        <div className="mt-3 rounded-xl border border-amber-500/40 bg-background p-3 text-xs" data-testid="fax-health-webhook-fix">
          <p>Incoming faxes are being turned away because the provider's callback isn't signed for this vault.</p>
          {canFix
            ? <button type="button" onClick={fix} disabled={busy} className="mt-2 rounded-full bg-primary text-primary-foreground px-3 py-1.5 font-medium disabled:opacity-50" data-testid="fax-health-fix-btn">Fix incoming faxes</button>
            : <p className="mt-1 text-muted-foreground" data-testid="fax-health-fix-hint">Open this page on {h.app_host} (the live site) as the owner and tap "Fix incoming faxes". Fixing it from here would point faxes at the wrong copy of the app.</p>}
        </div>
      )}
    </section>
  );
};
